/**
 * ProfileButton Component
 * Displays round profile button with user initials on the right side of the header
 * Shows loading/error states when profile data is unavailable
 */

import React from 'react';
import { useInitials } from './useInitials';
import { ProfileButtonProps } from './types';
import styles from './AppHeader.module.css';

/**
 * Renders a circular profile button with user initials
 * Handles loading, error, and success states
 * Semantic: button role with aria-label describing user identity
 */
export const ProfileButton: React.FC<ProfileButtonProps> = ({
  userProfile,
  className,
}) => {
  const initials = useInitials(userProfile);

  const getAriaLabel = (): string => {
    const { loadingState, firstName, lastName, email } = userProfile || {};

    if (loadingState === 'loading') {
      return 'Loading user profile';
    }

    if (loadingState === 'error') {
      return 'User profile error';
    }

    const name = firstName || lastName ? `${firstName || ''} ${lastName || ''}`.trim() : email;
    return `User profile: ${initials}${name ? ` (${name})` : ''}`;
  };

  const isLoading = userProfile?.loadingState === 'loading';
  const isError = userProfile?.loadingState === 'error';

  return (
    <button
      className={`${styles.profileButton} ${
        isLoading ? styles.loading : ''
      } ${isError ? styles.error : ''} ${className || ''}`}
      aria-label={getAriaLabel()}
      title={`User: ${userProfile?.firstName || 'User'}`}
      data-testid="profile-button"
    >
      {isLoading ? (
        <span className={styles.loadingSpinner} aria-hidden="true">
          ◉
        </span>
      ) : (
        <span className={styles.initials} data-testid="profile-initials">
          {initials}
        </span>
      )}
    </button>
  );
};

export default ProfileButton;
